import { useMemo } from 'react';
import { Dices, Pause, Play, Square, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { CANVAS_PRESETS, useStudio } from '../../store/studioStore';
import { useBatch } from '../../store/batchStore';
import { collectDimensions } from '../../bulk/dimensions';
import { STRATEGY_HINTS, STRATEGY_LABELS, type BatchStrategy, type PostMode } from '../../bulk/types';
import { CURATED_PALETTES } from '../../core/palette';
import { POST_EFFECTS } from '../../core/postfx/chain';
import { FONT_CATALOG } from '../../core/fonts/catalog';
import { listTechniques } from '../../techniques/registry';
import { Group, Row, Segmented, Select, Toggle } from '../controls/Basics';
import { Slider } from '../controls/Slider';

/** Multi-select chip list — used for every "which of these may appear" choice. */
function Chips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T[];
  onChange: (next: T[]) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1 py-1">
      {options.map((o) => {
        const active = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(active ? value.filter((v) => v !== o.value) : [...value, o.value])}
            className={clsx(
              'rounded px-1.5 py-0.5 text-[10px] transition-colors',
              active ? 'bg-ink-700 text-ink-100' : 'bg-ink-850 text-ink-500 hover:text-ink-300',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function BatchConfigPanel(): JSX.Element {
  const doc = useStudio((s) => s.doc);
  const config = useBatch((s) => s.config);
  const progress = useBatch((s) => s.progress);
  const plan = useBatch((s) => s.plan);
  const setConfig = useBatch((s) => s.setConfig);
  const setPostMode = useBatch((s) => s.setPostMode);
  const setTechniqueWeight = useBatch((s) => s.setTechniqueWeight);
  const rollPlanSeed = useBatch((s) => s.rollPlanSeed);
  const run = useBatch((s) => s.run);
  const pause = useBatch((s) => s.pause);
  const resume = useBatch((s) => s.resume);
  const cancel = useBatch((s) => s.cancel);
  const clear = useBatch((s) => s.clear);
  const variants = useBatch((s) => s.variants);

  const techniques = listTechniques();
  // Guard against an empty selection leaving the panel unusable — if every technique
  // got deselected, the run would silently produce text-only images.
  const selectedTechniques = config.techniqueIds.filter((id) => techniques.some((t) => t.id === id));
  const canRun = selectedTechniques.length > 0 || doc.layers.length > 1;

  const dimensions = useMemo(
    () =>
      collectDimensions(doc, {
        layerIds: new Set(config.includeLayerParams ? doc.layers.map((l) => l.id) : []),
        includeText: config.includeTextParams,
        includeBackground: config.includeBackgroundParams,
        includePost: config.includePostParams,
      }),
    [doc, config.includeLayerParams, config.includeTextParams, config.includeBackgroundParams, config.includePostParams],
  );

  // Grid produces the cartesian product, so its count is derived, not chosen.
  const plannedCount =
    config.strategy === 'grid' && config.gridAxes.length
      ? config.gridAxes.reduce((n, a) => n * Math.max(2, a.steps), 1)
      : config.count;

  // A rough figure is more honest than none: measured runs land around 120 ms per
  // preview-sized variant per worker.
  const estimateMs = (plannedCount * 130) / Math.max(1, config.workers);
  const estimateBytes = plannedCount * (config.previewSize * config.previewSize * 0.18);

  const running = progress.status === 'running';
  const paused = progress.status === 'paused';

  return (
    <div className="px-3 pb-6">
      <Group title="Strategie">
        <Row label="Verfahren">
          <Select
            value={config.strategy}
            onChange={(strategy: BatchStrategy) => setConfig({ strategy })}
            options={(Object.keys(STRATEGY_LABELS) as BatchStrategy[]).map((s) => ({
              value: s,
              label: STRATEGY_LABELS[s],
            }))}
          />
        </Row>
        <p className="pb-1 text-[10px] leading-relaxed text-ink-500">{STRATEGY_HINTS[config.strategy]}</p>

        {config.strategy !== 'grid' && (
          <Slider
            label="Varianten"
            value={config.count}
            onChange={(count) => setConfig({ count: Math.round(count) })}
            min={1}
            max={5000}
            step={1}
            scale="log"
            defaultValue={24}
            precision={0}
          />
        )}

        {config.strategy === 'seedSweep' && (
          <>
            <Slider label="Seed von" value={config.seedFrom} onChange={(seedFrom) => setConfig({ seedFrom: Math.round(seedFrom) })} min={0} max={999999999} step={1} precision={0} />
            <Slider label="Seed bis" value={config.seedTo} onChange={(seedTo) => setConfig({ seedTo: Math.round(seedTo) })} min={0} max={999999999} step={1} precision={0} />
          </>
        )}

        {config.strategy === 'mutation' && (
          <Slider
            label="Mutationsstärke"
            value={config.mutationStrength}
            onChange={(mutationStrength) => setConfig({ mutationStrength })}
            min={0}
            max={1}
            step={0.01}
            defaultValue={0.3}
            tooltip="Unter 25 % bleibt es in der Nachbarschaft, über 60 % wird frei neu gezogen."
          />
        )}

        {config.strategy === 'grid' && (
          <div className="space-y-1 py-1">
            <p className="text-[10px] text-ink-500">
              Ein bis drei Achsen. Ergibt {plannedCount} Bilder.
            </p>
            {[0, 1, 2].map((slot) => {
              const axis = config.gridAxes[slot];
              return (
                <div key={slot} className="flex items-center gap-1">
                  <div className="min-w-0 flex-1">
                    <Select
                      value={axis?.dimensionId ?? ''}
                      onChange={(dimensionId) => {
                        const next = [...config.gridAxes];
                        if (!dimensionId) next.splice(slot, 1);
                        else next[slot] = { dimensionId, steps: axis?.steps ?? 5 };
                        setConfig({ gridAxes: next.filter(Boolean) });
                      }}
                      options={[
                        { value: '', label: `Achse ${slot + 1} — keine` },
                        ...dimensions.map((d) => ({ value: d.id, label: d.label })),
                      ]}
                    />
                  </div>
                  <input
                    type="number"
                    min={2}
                    max={40}
                    value={axis?.steps ?? 5}
                    disabled={!axis}
                    onChange={(e) => {
                      const next = [...config.gridAxes];
                      if (next[slot]) next[slot] = { ...next[slot], steps: Math.max(2, Number(e.target.value) || 2) };
                      setConfig({ gridAxes: next });
                    }}
                    className="field-input w-14 shrink-0 text-center font-mono text-[11px]"
                  />
                </div>
              );
            })}
          </div>
        )}
      </Group>

      <Group title="Was variiert">
        <Toggle checked={config.includeLayerParams} onChange={(v) => setConfig({ includeLayerParams: v })} label="Technik-Parameter" tooltip="Alle nicht gesperrten Parameter der Layer, innerhalb ihres Explorationsbereichs." />
        <Toggle checked={config.includeTextParams} onChange={(v) => setConfig({ includeTextParams: v })} label="Text-Parameter" />
        <Toggle checked={config.includeBackgroundParams} onChange={(v) => setConfig({ includeBackgroundParams: v })} label="Hintergrund-Parameter" />
        <Toggle checked={config.includePostParams} onChange={(v) => setConfig({ includePostParams: v })} label="Post-FX-Parameter" />
        <p className="pt-1 text-[10px] leading-relaxed text-ink-500">
          {dimensions.length} variable Parameter. Gesperrte bleiben fest; die Bereiche kommen aus den Reglern im
          Studio.
        </p>
      </Group>

      <Group title="Techniken" defaultOpen={false}>
        <Chips
          options={techniques.map((t) => ({ value: t.id, label: t.name }))}
          value={config.techniqueIds}
          onChange={(techniqueIds) => setConfig({ techniqueIds })}
        />
        <Toggle
          checked={config.combineLayers}
          onChange={(combineLayers) => setConfig({ combineLayers })}
          label="Layer je Bild neu kombinieren"
          tooltip="Aus: der Layer-Stapel aus dem Studio bleibt erhalten."
        />
        {config.combineLayers && (
          <>
            <Slider label="Min. Layer" value={config.minLayers} onChange={(v) => setConfig({ minLayers: Math.round(v) })} min={1} max={5} step={1} precision={0} />
            <Slider label="Max. Layer" value={config.maxLayers} onChange={(v) => setConfig({ maxLayers: Math.round(v) })} min={1} max={5} step={1} precision={0} />
            {config.techniqueIds.map((id) => {
              const technique = techniques.find((t) => t.id === id);
              if (!technique) return null;
              return (
                <Slider
                  key={id}
                  label={`Gewicht: ${technique.name}`}
                  value={config.techniqueWeights[id] ?? 1}
                  onChange={(w) => setTechniqueWeight(id, w)}
                  min={0}
                  max={5}
                  step={0.1}
                  defaultValue={1}
                />
              );
            })}
          </>
        )}
      </Group>

      <Group title="Text, Font, Format" defaultOpen={false}>
        <Toggle checked={config.varyText} onChange={(varyText) => setConfig({ varyText })} label="Wörter variieren" />
        {config.varyText && (
          <textarea
            rows={4}
            value={config.words.join('\n')}
            onChange={(e) => setConfig({ words: e.target.value.split('\n').filter((w) => w.trim()) })}
            placeholder="Ein Wort pro Zeile"
            className="field-input resize-y font-mono text-[11px]"
          />
        )}
        <Toggle checked={config.varyFonts} onChange={(varyFonts) => setConfig({ varyFonts })} label="Schriften variieren" />
        {config.varyFonts && (
          <Chips
            options={FONT_CATALOG.map((f) => ({ value: f.id, label: f.family }))}
            value={config.fontIds}
            onChange={(fontIds) => setConfig({ fontIds })}
          />
        )}
        <Toggle checked={config.varyCanvas} onChange={(varyCanvas) => setConfig({ varyCanvas })} label="Formate variieren" />
        {config.varyCanvas && (
          <Chips
            options={CANVAS_PRESETS.map((p) => ({ value: p.id, label: p.label }))}
            value={config.canvasPresetIds}
            onChange={(canvasPresetIds) => setConfig({ canvasPresetIds })}
          />
        )}
      </Group>

      <Group title="Farbe & Hintergrund" defaultOpen={false}>
        <Toggle checked={config.varyPalettes} onChange={(varyPalettes) => setConfig({ varyPalettes })} label="Paletten variieren" />
        {config.varyPalettes && (
          <Chips
            options={CURATED_PALETTES.map((p) => ({ value: p.id, label: p.name }))}
            value={config.paletteIds}
            onChange={(paletteIds) => setConfig({ paletteIds })}
          />
        )}
        <Toggle checked={config.varyBackground} onChange={(varyBackground) => setConfig({ varyBackground })} label="Hintergründe variieren" />
        {config.varyBackground && (
          <Chips
            options={[
              { value: 'solid', label: 'Vollton' },
              { value: 'linear', label: 'Linear' },
              { value: 'radial', label: 'Radial' },
              { value: 'noise', label: 'Rauschen' },
            ]}
            value={config.backgroundKinds}
            onChange={(backgroundKinds) => setConfig({ backgroundKinds })}
          />
        )}
      </Group>

      <Group title="Post-Processing" defaultOpen={false}>
        {POST_EFFECTS.map((effect) => (
          <div key={effect.id} className="flex items-center gap-2 py-0.5">
            <span className="min-w-0 flex-1 truncate text-[11px] text-ink-300">{effect.name}</span>
            <div className="w-[136px] shrink-0">
              <Segmented
                size="sm"
                value={config.postMode[effect.id] ?? 'off'}
                onChange={(mode: PostMode) => setPostMode(effect.id, mode)}
                options={[
                  { value: 'off', label: 'Aus' },
                  { value: 'on', label: 'An' },
                  { value: 'random', label: 'Zufall' },
                ]}
              />
            </div>
          </div>
        ))}
        <Slider
          label="Wahrscheinlichkeit"
          value={config.postProbability}
          onChange={(postProbability) => setConfig({ postProbability })}
          min={0}
          max={1}
          step={0.01}
          defaultValue={0.4}
          tooltip="Gilt für alle Effekte im Modus „Zufall“."
        />
      </Group>

      <Group title="Rendering">
        <Slider
          label="Vorschaugrösse"
          value={config.previewSize}
          onChange={(previewSize) => setConfig({ previewSize: Math.round(previewSize) })}
          min={128}
          max={1200}
          step={4}
          defaultValue={420}
          unit="px"
          precision={0}
          tooltip="Längste Kante der Galerie-Thumbnails. Der Export rendert separat neu."
        />
        <Slider
          label="Parallele Worker"
          value={config.workers}
          onChange={(workers) => setConfig({ workers: Math.round(workers) })}
          min={1}
          max={Math.max(2, navigator.hardwareConcurrency || 8)}
          step={1}
          precision={0}
          tooltip={`Dieser Rechner meldet ${navigator.hardwareConcurrency || '?'} Kerne.`}
        />
      </Group>

      {/* ── Run controls ──────────────────────────────────────────────────── */}
      <div className="sticky bottom-0 -mx-3 mt-3 border-t border-ink-800 bg-ink-900 px-3 pb-3 pt-3">
        <div className="mb-2 rounded-md bg-ink-850 px-2.5 py-2 font-mono text-[10px] leading-relaxed text-ink-400">
          <div className="text-ink-200">{plannedCount} Varianten</div>
          <div className="text-ink-600">
            ≈ {formatDuration(estimateMs)} · ≈ {(estimateBytes / 1024 / 1024).toFixed(0)} MB Vorschau
          </div>
          {plan && <div className="text-ink-600">{plan.note}</div>}
        </div>

        {plannedCount > 800 && (
          <p className="mb-2 rounded-md border border-amber-900/50 bg-amber-950/40 px-2.5 py-1.5 text-[10px] leading-relaxed text-amber-200">
            {plannedCount} Varianten brauchen spürbar Zeit und Speicher. Für einen ersten Eindruck lohnt ein
            kleiner Lauf.
          </p>
        )}

        <div className="flex gap-1">
          {!running && !paused && (
            <button
              type="button"
              onClick={() => run(doc)}
              disabled={!canRun}
              title={canRun ? undefined : 'Mindestens eine Technik auswählen oder im Studio einen Layer anlegen.'}
              className={clsx(
                'flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-[12px] font-medium transition-colors',
                !canRun ? 'bg-ink-750 text-ink-500' : 'bg-accent-500 text-ink-950 hover:bg-accent-400',
              )}
            >
              <Play size={13} />
              Starten
            </button>
          )}
          {running && (
            <button type="button" onClick={pause} className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-ink-700 py-2 text-[12px] text-ink-100 hover:bg-ink-600">
              <Pause size={13} />
              Pause
            </button>
          )}
          {paused && (
            <button type="button" onClick={resume} className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-accent-500 py-2 text-[12px] font-medium text-ink-950 hover:bg-accent-400">
              <Play size={13} />
              Fortsetzen
            </button>
          )}
          {(running || paused) && (
            <button type="button" onClick={cancel} title="Abbrechen" className="ghost-button w-9 justify-center rounded-md bg-ink-800 py-2">
              <Square size={12} />
            </button>
          )}
          <button type="button" onClick={rollPlanSeed} title="Anderen Plan würfeln" className="ghost-button w-9 justify-center rounded-md bg-ink-800 py-2">
            <Dices size={13} />
          </button>
          {variants.length > 0 && !running && (
            <button type="button" onClick={clear} title="Ergebnisse verwerfen" className="ghost-button w-9 justify-center rounded-md bg-ink-800 py-2 hover:text-red-400">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 90) return `${s.toFixed(0)} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${Math.round(s - m * 60)} s`;
}
